import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, X } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Alert, Badge, Card, CardContent, Input, Select, Skeleton } from '@/components/ui/primitives';
import { api, errorMessage } from '@/lib/api';
import { formatDateTime } from '@/lib/utils';

interface ChangeItem {
  id: string;
  kind: 'NEW' | 'CHANGE';
  status: 'OPEN' | 'DONE' | 'DISMISSED';
  source: string;
  changes: { field: string; label: string; old: string | null; new: string | null }[];
  customer: { id: string; number: string; name: string; erpNumber: string | null };
  resolution: string | null;
  resolvedByName: string | null;
  resolvedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

const SALUTATION_LABELS: Record<string, string> = { MR: 'Herr', MRS: 'Frau', FAMILY: 'Familie' };
const shown = (field: string, v: string | null) => (field === 'salutation' && v ? (SALUTATION_LABELS[v] ?? v) : v);

/** Hinweise an die Buchhaltung: neue Kunden bzw. geänderte Stammdaten in Powerbird nachtragen. */
export function CustomerChangesTab() {
  const qc = useQueryClient();
  const [status, setStatus] = useState<'OPEN' | 'DONE' | 'DISMISSED' | 'ALL'>('OPEN');
  const [erpNumbers, setErpNumbers] = useState<Record<string, string>>({});
  const { data, isLoading } = useQuery({ queryKey: ['customer-changes', status], queryFn: () => api<{ items: ChangeItem[]; open: number }>(`/customer-changes?status=${status}`) });
  const resolve = useMutation({
    mutationFn: ({ id, action, erpNumber }: { id: string; action: 'done' | 'dismiss'; erpNumber?: string }) =>
      api(`/customer-changes/${id}/resolve`, { method: 'POST', body: { action, erpNumber: erpNumber || undefined } }),
    onSuccess: () => {
      toast.success('Hinweis erledigt');
      qc.invalidateQueries({ queryKey: ['customer-changes'] });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  return (
    <div className="grid gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">
          Im Portal neu angelegte Kunden und Änderungen an Powerbird-Kunden – bitte in Powerbird nachtragen. Stimmt Powerbird beim nächsten Abgleich mit der Änderung überein, erledigt sich der Hinweis selbst.
        </p>
        <Select className="w-48" value={status} onChange={(e) => setStatus(e.target.value as typeof status)} aria-label="Status">
          <option value="OPEN">Offen</option>
          <option value="DONE">Erledigt</option>
          <option value="DISMISSED">Verworfen</option>
          <option value="ALL">Alle</option>
        </Select>
      </div>
      {isLoading && <Skeleton className="h-40" />}
      {data?.items.length === 0 && <Alert>{status === 'OPEN' ? 'Keine offenen Hinweise.' : 'Keine Einträge.'}</Alert>}
      {data?.items.map((c) => (
        <Card key={c.id}>
          <CardContent className="grid gap-3 pt-5">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant={c.kind === 'NEW' ? 'default' : 'outline'}>{c.kind === 'NEW' ? 'Neuer Kunde' : 'Änderung'}</Badge>
                  <Link to={`/customers/${c.customer.id}`} className="font-medium hover:underline">
                    {c.customer.name}
                  </Link>
                  <span className="text-xs text-muted-foreground">
                    Portal {c.customer.number}
                    {c.customer.erpNumber ? ` · Powerbird ${c.customer.erpNumber}` : ' · noch nicht in Powerbird'}
                  </span>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  {formatDateTime(c.createdAt)} von {c.source}
                  {c.updatedAt !== c.createdAt && ` · ergänzt ${formatDateTime(c.updatedAt)}`}
                </p>
              </div>
              {c.status !== 'OPEN' && (
                <p className="text-xs text-muted-foreground">
                  {c.status === 'DONE' ? 'Erledigt' : 'Verworfen'} {c.resolvedAt && formatDateTime(c.resolvedAt)} {c.resolvedByName && `von ${c.resolvedByName}`}
                  {c.resolution && ` – ${c.resolution}`}
                </p>
              )}
            </div>
            <div className="overflow-x-auto rounded-md border">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-xs text-muted-foreground">
                  <tr>
                    <th className="px-3 py-1.5 text-left font-medium">Feld</th>
                    {c.kind === 'CHANGE' && <th className="px-3 py-1.5 text-left font-medium">Bisher (Powerbird)</th>}
                    <th className="px-3 py-1.5 text-left font-medium">{c.kind === 'CHANGE' ? 'Neu' : 'Angabe'}</th>
                  </tr>
                </thead>
                <tbody>
                  {c.changes.map((x) => (
                    <tr key={x.field} className="border-t">
                      <td className="px-3 py-1.5 text-muted-foreground">{x.label}</td>
                      {c.kind === 'CHANGE' && <td className="px-3 py-1.5 text-muted-foreground line-through decoration-muted-foreground/50">{shown(x.field, x.old) ?? '–'}</td>}
                      <td className="px-3 py-1.5 font-medium">{shown(x.field, x.new) ?? <span className="font-normal text-muted-foreground">(gelöscht)</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {c.status === 'OPEN' && (
              <div className="flex flex-wrap items-center justify-end gap-2">
                {c.kind === 'NEW' && (
                  <Input
                    className="h-8 w-56"
                    placeholder="Powerbird-Kundennummer (optional)"
                    value={erpNumbers[c.id] ?? ''}
                    onChange={(e) => setErpNumbers((m) => ({ ...m, [c.id]: e.target.value }))}
                    aria-label="Powerbird-Kundennummer"
                  />
                )}
                <Button size="sm" variant="ghost" onClick={() => resolve.mutate({ id: c.id, action: 'dismiss' })}>
                  <X /> Verwerfen
                </Button>
                <Button size="sm" onClick={() => resolve.mutate({ id: c.id, action: 'done', erpNumber: erpNumbers[c.id]?.trim() })} loading={resolve.isPending && resolve.variables?.id === c.id}>
                  <CheckCircle2 /> In Powerbird nachgetragen
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
